import type { Banner, Category, Exam, Product, User } from './types';

export const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:3000';

let authToken: string | null = null;
export function setAuthToken(token: string | null) {
  authToken = token;
}

export class ApiError extends Error {
  status: number;
  /** The parsed error payload, so callers can read flags like `needsProfile`. */
  body: Record<string, unknown>;
  constructor(message: string, status: number, body: Record<string, unknown> = {}) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, { ...options, headers });
  } catch {
    throw new ApiError('Network error — is the API server running?', 0);
  }
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) throw new ApiError(data?.error || `Request failed (${res.status})`, res.status, data);
  return data as T;
}

export type BootstrapResponse = {
  products: Product[];
  categories: Category[];
  exams: Exam[];
  banners: Banner[];
};

export type PracticeKind = 'quiz' | 'pyq' | 'testseries';

export type RecentAttempt = {
  id: string;
  type: PracticeKind;
  paper?: string | null;
  score: number;
  total: number;
  percent: number;
  marks: number;
  maxMarks: number;
  completedAt: string;
};

export type PaperInfo = {
  paper: string;
  title: string;
  count: number;
  durationMin: number;
  marksCorrect: number;
  marksWrong: number;
};

export type PracticeInfo = {
  product: { id: string; title: string; type: string; emoji: string; gradient: string[] };
  kind: PracticeKind;
  access: boolean;
  recent: RecentAttempt[];
  available?: number; // quiz | pyq
  sizes?: number[]; // quiz | pyq
  years?: string[]; // pyq
  papers?: PaperInfo[]; // testseries
};

export type PlayQuestion = { id: string; text: string; options: string[]; year?: string; session?: string };

export type PracticeSession =
  | {
      id: string;
      status: 'in_progress';
      type: PracticeKind;
      size: number;
      questions: PlayQuestion[];
      paper?: string;
      durationMin?: number;
      marksCorrect?: number;
      marksWrong?: number;
      startedAt?: string;
    }
  | PracticeReview;

export type ReviewQuestion = {
  id: string;
  text: string;
  options: string[];
  answer: number;
  solution: string;
  your: number | null;
  correct: boolean;
  year?: string;
  session?: string;
};

export type PracticeReview = {
  id: string;
  status: 'completed';
  type: PracticeKind;
  score: number;
  total: number;
  percent: number;
  correct: number;
  wrong: number;
  skipped: number;
  questions: ReviewQuestion[];
  paper?: string;
  marks?: number;
  maxMarks?: number;
  marksCorrect?: number;
  marksWrong?: number;
  durationMin?: number;
};

export type StartOpts = { size?: number; year?: string; paper?: string };

export const api = {
  health: () => request<{ ok: boolean; storage: string; payments: string }>('/api/health'),
  bootstrap: () => request<BootstrapResponse>('/api/bootstrap'),

  // Email is the login key. `name`/`phone` are only needed the first time an
  // address is seen — the server replies `needsProfile` when it wants them.
  sendOtp: (email: string, profile?: { name: string; phone: string }) =>
    request<{ success: boolean; registered: boolean; message: string; demoOtp?: string }>('/api/auth/send-otp', {
      method: 'POST',
      body: JSON.stringify({ email, ...(profile ?? {}) }),
    }),
  verifyOtp: (email: string, otp: string) =>
    request<{ token: string; user: User & { id: string; library: string[] } }>('/api/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ email, otp }),
    }),
  me: () => request<{ user: User & { id: string; library: string[] } }>('/api/me'),
  library: () => request<{ productIds: string[]; items: Product[] }>('/api/library'),

  createPayment: (productIds: string[]) =>
    request<
      | { mode: 'free' | 'mock'; library: string[] }
      | { mode: 'razorpay'; orderId: string; amount: number; currency: string; keyId: string; checkoutUrl: string }
    >('/api/payments/create-order', { method: 'POST', body: JSON.stringify({ productIds }) }),
  verifyPayment: (fields: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) =>
    request<{ success: boolean; library: string[] }>('/api/payments/verify', {
      method: 'POST',
      body: JSON.stringify(fields),
    }),

  getProductFiles: (productId: string) =>
    request<{ productId: string; files: { index: number; title: string; url: string; viewer: 'webview' | 'browser' }[] }>(
      `/api/library/${productId}/files`
    ),

  practiceInfo: (productId: string) => request<PracticeInfo>(`/api/practice/products/${productId}`),
  startSession: (productId: string, opts: StartOpts) =>
    request<{ sessionId: string }>('/api/practice/sessions', {
      method: 'POST',
      body: JSON.stringify({ productId, ...opts }),
    }),
  getSession: (id: string) => request<PracticeSession>(`/api/practice/sessions/${id}`),
  submitSession: (id: string, answers: Record<string, number>) =>
    request<PracticeReview>(`/api/practice/sessions/${id}/submit`, { method: 'POST', body: JSON.stringify({ answers }) }),

  getBookmarks: () => request<{ ids: string[]; questions: BookmarkQuestion[] }>('/api/practice/bookmarks'),
  toggleBookmark: (questionId: string) =>
    request<{ bookmarked: boolean; count: number }>(`/api/practice/bookmarks/${questionId}`, { method: 'POST' }),
};

export type BookmarkQuestion = {
  id: string;
  productId: string;
  type: PracticeKind;
  text: string;
  options: string[];
  answer: number;
  solution: string;
  year?: string;
  session?: string;
};
